import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

export interface DateCalculatorState {
  // Input date
  startDate: string; // ISO date string
  
  // Calculation type
  calculationType: 'add' | 'subtract';
  
  // Amount to add/subtract
  days: number;
  weeks: number;
  months: number;
  years: number;
  
  // Result
  resultDate: string | null;
  
  // UI state
  error: string | null;
}

const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

const initialState: DateCalculatorState = {
  startDate: today,
  calculationType: 'add',
  days: 0,
  weeks: 0,
  months: 0,
  years: 0,
  resultDate: null,
  error: null,
};

export const DateCalculatorSlice = createSlice({
  name: "dateCalculator",
  initialState,
  reducers: {
    startDateChanged: (state, action: PayloadAction<string>) => {
      state.startDate = action.payload;
      state.error = null;
      state.resultDate = null;
    },
    
    calculationTypeChanged: (state, action: PayloadAction<'add' | 'subtract'>) => {
      state.calculationType = action.payload;
      state.error = null;
      state.resultDate = null;
    },
    
    daysChanged: (state, action: PayloadAction<number>) => {
      state.days = Math.max(0, action.payload);
      state.error = null;
      state.resultDate = null;
    },
    
    weeksChanged: (state, action: PayloadAction<number>) => {
      state.weeks = Math.max(0, action.payload);
      state.error = null;
      state.resultDate = null;
    },
    
    monthsChanged: (state, action: PayloadAction<number>) => {
      state.months = Math.max(0, action.payload);
      state.error = null;
      state.resultDate = null;
    },
    
    yearsChanged: (state, action: PayloadAction<number>) => {
      state.years = Math.max(0, action.payload);
      state.error = null;
      state.resultDate = null;
    },
    
    calculateDate: (state) => {
      try {
        const startDate = new Date(state.startDate);
        
        if (isNaN(startDate.getTime())) {
          state.error = "Please enter a valid date";
          state.resultDate = null;
          return;
        }
        
        const resultDate = new Date(startDate);
        
        // Calculate total days from weeks
        const totalDays = state.days + (state.weeks * 7);
        
        if (state.calculationType === 'add') {
          // Add years
          resultDate.setFullYear(resultDate.getFullYear() + state.years);
          
          // Add months
          resultDate.setMonth(resultDate.getMonth() + state.months);
          
          // Add days
          resultDate.setDate(resultDate.getDate() + totalDays);
        } else {
          // Subtract years
          resultDate.setFullYear(resultDate.getFullYear() - state.years);
          
          // Subtract months
          resultDate.setMonth(resultDate.getMonth() - state.months);
          
          // Subtract days
          resultDate.setDate(resultDate.getDate() - totalDays);
        }
        
        state.resultDate = resultDate.toISOString().split('T')[0];
        state.error = null;
      } catch (error) {
        state.error = "Error calculating date";
        state.resultDate = null;
      }
    },
    
    setQuickCalculation: (state, action: PayloadAction<{ days: number; weeks: number; months: number; years: number }>) => {
      state.days = action.payload.days;
      state.weeks = action.payload.weeks;
      state.months = action.payload.months;
      state.years = action.payload.years;
      state.error = null;
      state.resultDate = null;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    resetCalculator: (state) => {
      return { ...initialState };
    },
  },
});

// Utility functions
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getDaysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function isWeekend(dateString: string): boolean {
  const date = new Date(dateString);
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday = 0, Saturday = 6
}

export function isBusinessDay(dateString: string): boolean {
  return !isWeekend(dateString);
}

export const {
  startDateChanged,
  calculationTypeChanged,
  daysChanged,
  weeksChanged,
  monthsChanged,
  yearsChanged,
  calculateDate,
  setQuickCalculation,
  clearError,
  resetCalculator,
} = DateCalculatorSlice.actions;

export const selectState = (state: RootState) => state.dateCalculator;

export default DateCalculatorSlice.reducer;

